import {useSelector} from 'react-redux'

import {selectUser} from 'features/User'
import {DOPE_PRIMARY_DOMAIN} from 'definitions/constant'
import {EnvironmentEnum} from 'interfaces'
import {useEntitlementsQuery} from 'features/Entitlements/hooks'
import {selectSSLInspectionDisabledInData} from 'features/Policies/policiesSelectors'
import {useSSLInspectionRelease} from './useSSLInspectionRelease'

/** O365 Copilot controls are hidden for these tenant primary domains. */
const COPILOT_CONTROLS_HIDDEN_PRIMARY_DOMAINS = new Set([
  'tjx.com',
  'havebury.com',
  'successacademies.org',
  'monolithicpower.com',
  'mpsmail.onmicrosoft.com',
])

interface UseFeatureFlagReturnType {
  isDopeTenant: boolean
  isPaywalled: boolean
  developmentOnlyFeatureFlag: boolean
  testOnlyFeatureFlag: boolean
  stagingOnlyFeatureFlag: boolean
  deploymentOnlyFeatureFlag: boolean
  nonProductionFeatureFlag: boolean
  dopeInternalFeatureFlag: boolean
  productionDopeFeatureFlag: boolean
  swgDlpBlock: boolean
  isSSLInspectionToggleFeatureFlag: boolean
  isSSLInspectionDisabled: boolean
  /** Tenant gate for O365 Copilot CAC controls (combine with O365 app selection in UI). */
  isO365CopilotControlsEnabledForTenant: boolean
}

/**
 * @description This hook is used to get the feature flag values
 */
export const useFeatureFlag = (): UseFeatureFlagReturnType => {
  const user = useSelector(selectUser)
  const {isSSLInspectionToggleReleased} = useSSLInspectionRelease()

  const isSSLInspectionDisabled = useSelector(selectSSLInspectionDisabledInData)
  const {casbFullAccess, swgDlpBlock, isLoading} = useEntitlementsQuery({
    enabled: false,
  })

  // true if the user does not have CASB full access and the entitlements are not loading
  const isPaywalled = !casbFullAccess && !isLoading

  const primaryDomain = user?.primaryDomain

  /**
   * Default global feature flags for the application.
   */
  const isDopeTenant = primaryDomain === DOPE_PRIMARY_DOMAIN

  const developmentOnlyFeatureFlag =
    process.env.REACT_APP_ENV === EnvironmentEnum.DEVELOPMENT

  const testOnlyFeatureFlag = process.env.REACT_APP_ENV === EnvironmentEnum.TEST

  const deploymentOnlyFeatureFlag =
    process.env.REACT_APP_ENV === EnvironmentEnum.DEPLOYMENT

  const stagingOnlyFeatureFlag =
    process.env.REACT_APP_ENV === EnvironmentEnum.STAGING

  const nonProductionFeatureFlag =
    process.env.REACT_APP_ENV !== EnvironmentEnum.PRODUCTION

  // true if the environment is not production and primaryDomain is dope.security
  const dopeInternalFeatureFlag = nonProductionFeatureFlag && isDopeTenant

  // true if the environment is production and primaryDomain is dope.security
  const productionDopeFeatureFlag = !nonProductionFeatureFlag && isDopeTenant

  /**
   * Individual feature flags for different features on the platform.
   */

  // SSL Inspection toggle feature flag
  const isSSLInspectionToggleFeatureFlag =
    nonProductionFeatureFlag || isSSLInspectionToggleReleased

  const isO365CopilotControlsEnabledForTenant =
    !COPILOT_CONTROLS_HIDDEN_PRIMARY_DOMAINS.has(
      (primaryDomain ?? '').toLowerCase(),
    )

  return {
    nonProductionFeatureFlag,
    isDopeTenant,
    developmentOnlyFeatureFlag,
    deploymentOnlyFeatureFlag,
    testOnlyFeatureFlag,
    stagingOnlyFeatureFlag,
    dopeInternalFeatureFlag,
    productionDopeFeatureFlag,
    isPaywalled,
    swgDlpBlock,
    isSSLInspectionToggleFeatureFlag,
    isSSLInspectionDisabled:
      isSSLInspectionToggleFeatureFlag && isSSLInspectionDisabled,
    isO365CopilotControlsEnabledForTenant,
  }
}
